// eslint-disable-next-line no-unused-vars
import React, { useRef, useEffect } from 'react';
import { Button, Checkbox, Form, Loading, TextInput } from '@carbon/react';
import uniqueId from 'lodash/uniqueId';
import { bool, array, func, string, object } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { CircleCheck, Info, ChevronDown } from 'lucide-react';

import { NWTextInput } from 'Components/shared';

const StyledFormContainer = styled.div`
  flex-grow: 1;
  background-color: #ffffff00;
  padding: 24px;

  p {
    padding-right: 0;
  }

  &.loginPage {
    padding: ${ props => (!props.sessionExpired && props.success) ? '26px 65px' : '26px 126px' };
    .cds--text-input-wrapper {
      margin-top: 24px;
    }
  }

  &.reset {
    .buttons {
      width: 100%;
      margin-top: 15px;
      display: flex;
      flex-direction: column;

      button {
        font-size: 1rem;
        margin: 0;
        height: 40px;

        &.backButton {
          background: transparent;
          border: none;
          color: #0f62fe;
          font-size: 14px;
          margin-top: 48px;
          cursor: pointer;

          svg {
            transform: rotate(90deg);
            stroke: #0f233c;
            margin-right: 5px;
          }
        }
      }
    }
  }

  .cds--label {
    margin: 6px 0;
  }

  .messagePlaceholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 28px;
    text-align: center;

    .title {
      font-size: 1.75rem;
      color: inherit;
      width: 100%;
      font-weight: 700;
    }

    p {
      font-size: 0.875rem;
      line-height: 1.25rem;
      padding: 0;
    }
  }

  .successMessage {
    text-align: center;
    display: flex;
    justify-content: center;
    flex-direction: column;
    padding: 20px 0 100px;
  }

  form {
    color: inherit;

    .submit {
      width: 100%;
      height: 40px;
      max-width: none;
      margin-top: 24px;
      padding: 0;
      justify-content: center;
      font-size: 1rem;
      text-align: center;

      .cds--loading {
        left: calc(50% - 75px);
      }

      svg {
        margin-right: 5px;
      }

      background-color: ${ props => (!props.sessionExpired && props.success)
        ? props.theme.colors.green
        : props.theme.colors.red };
    }

    .loginHelpers {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .cds--checkbox-label {
        &:before {
          border-color: inherit;
        }
      }

      button {
        &:active, &:focus {
          font-weight: 600;
        }

        color: inherit;
        outline: 0 none;
        border: 0 none;
        padding: 0;
        box-shadow: none;
        background: transparent;
      }
    }
  }

  .mfaSection {
    margin-top: 10px;

    .mfaInput {
      display: flex;
      align-items: center;
      gap: 5px;

      span {
        cursor: pointer;
        margin-top: 25px;
      }
      
      .verificationTooltip {
        max-width: 300px;
      }
    }

    .mfaSubmit {
        width: 100%;
        height: 40px;
        max-width: none;
        margin-top: 10px;
        padding: 0;
        justify-content: center;
        font-size: 1rem;
        text-align: center;
  
        .cds--loading {
          left: calc(50% - 75px);
        }
  
        svg {
          margin-right: 5px;
        }
      }
  }

  @media screen and (max-width: 767px) {
    &.loginPage {
      padding: 10px 20px;

      .cds--text-input-wrapper {
        margin-top: 16px;
      }
    }
  }
`;

const StyledLoading = styled(Loading)`
  width: 1em;
  height: 1em;
  margin-left: 1em;
`;

const StyledTextInput = styled(TextInput)`
  margin: 6px auto;
  border-radius: 3px;
`;

const fields = {
  authCode: 'authCode',
};

const LoginForm = props => {
  const {
    title,
    message,
    loading,
    success,
    successMessages,
    recovered,
    error,
    mfaVerifyError,
    sessionExpired,
    rememberMe,
    onChange,
    username,
    password,
    recoveryEmail,
    onRemember,
    onSubmit,
    isRecoveryMode,
    onRecoverPasswordSubmit,
    onRecovery,
    onCancelRecovery,
    isLoginPage,
    onLoginVerify,
    validators,
    register,
    rhfErrors,
    loadingVerify,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const mfaSectionRef = useRef(null);

  useEffect(() => {
    if (!sessionExpired && mfaSectionRef.current) {
      const input = mfaSectionRef.current.querySelector('input');
      if (input) {
        setTimeout(() => input.focus(), 50);
      }
    }
  }, [sessionExpired]);

  const sharedProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: !!mfaVerifyError || !!rhfErrors[fieldName]?.message.length,
    invalidText: mfaVerifyError || rhfErrors[fieldName]?.message,
    ...register(fieldName, { ...validators[fieldName] }),
  });

  const renderSubmitIcon = () => {
    if (loading) {
      return <StyledLoading small withOverlay={ false } />;
    }

    if (success) {
      return <CircleCheck size={32} />;
    }

    return null;
  };

  const renderButtonMessage = () => {
    if (loading) {
      if (success) {
        return t('profile.loginSuccess');
      }
      return `${t('profile.loadingLogin')}...`;
    }
    //
    // if (error) {
    //   return t('profile.loginError');
    // }

    if (success) {
      return t('profile.loginSuccess');
    }

    return t('profile.login');
  };

  if (isRecoveryMode) {
    return (
      <StyledFormContainer
        className={ `formContainer reset ${ isLoginPage ? 'loginPage' : '' }` }
        theme={ theme }
        success={ recovered }
        error={ error }
      >
        { recovered ? (
          <div className="successMessage">
            <h3>{ t('profile.passwordRecoveryEmailSent') }</h3>
            <h4>{ t('profile.redirectMessage') }</h4>
          </div>
        ) : (
          <>
            <div className="messagePlaceholder">
              <h3 className="title">{ t('profile.passwordRecovery') }</h3>
              <p>{ t('profile.passwordRecoveryBody') }</p>
            </div>
            <Form method="post">
              <StyledTextInput
                autoFocus
                data-modal-primary-focus
                id={ uniqueId('email-') }
                name="email"
                disabled={ loading }
                labelText={ t('profile.emailPlaceholder') }
                placeholder={ t('profile.emailPlaceholder') }
                type="email"
                required
                onChange={ onChange('recovery_email') }
                value={ recoveryEmail }
                invalid={ !!error }
              />
              <p className="error" dangerouslySetInnerHTML={{ __html: error }} />
              <div className="buttons">
                <Button
                  className="red submit"
                  error={ error }
                  type="submit"
                  disabled={ loading }
                  onClick={ onRecoverPasswordSubmit }
                  kind="danger"
                >
                  { loading && (
                    <StyledLoading small withOverlay={ false } />
                  ) }
                  { t('common.send') }
                </Button>
                <button
                  className="backButton"
                  type="button"
                  disabled={ loading }
                  onClick={ onCancelRecovery }
                >
                  <ChevronDown />{ t('common.backToPrevScreen') }
                </button>
              </div>
            </Form>
          </>
        ) }
      </StyledFormContainer>
    );
  }

  return (
    <StyledFormContainer
      className={ `formContainer ${ isLoginPage ? 'loginPage' : '' }` }
      theme={ theme }
      success={ success }
      error={ error }
      sessionExpired={ sessionExpired }
    >
      <div className="messagePlaceholder">
        { title && (<h3 className="title">{ title }</h3>) }
        { message && <p dangerouslySetInnerHTML={{ __html: message }} /> }
        { !message && !successMessages?.length && (
          <>
            <h3 className="title">{ t('profile.welcome') }!</h3>
            <p>{ t('profile.demandLogin') }</p>
          </>
        ) }
      </div>
      { (successMessages || []).map(msg => <p dangerouslySetInnerHTML={{ __html: msg }} />) }
      <Form method="post">
        <StyledTextInput
          autoFocus
          data-modal-primary-focus
          id={ uniqueId('email-') }
          name="email"
          disabled={ loading || !sessionExpired }
          labelText={ t('profile.emailPlaceholder') }
          placeholder={ t('profile.emailPlaceholder') }
          type="email"
          required
          onChange={ onChange('username') }
          value={ username }
          invalid={ !!error }
        />
        <StyledTextInput
          id={ uniqueId('password-') }
          type="password"
          disabled={ loading || !sessionExpired }
          labelText={ t('profile.passwordPlaceholder') }
          name="password"
          placeholder={ t('profile.passwordPlaceholder') }
          onChange={ onChange('password') }
          required
          value={ password }
          invalid={ !!error }
          invalidText={ error }
        />
        { sessionExpired && (
          <>
            <div className="loginHelpers">
              <Checkbox
                id={ uniqueId('rememberMe') }
                name="_remember_me"
                labelText={ t('profile.rememberMe') }
                onChange={ (_, { checked }) => onRemember(checked) }
                checked={ rememberMe }
                value={ rememberMe }
              />
              <Button
                kind="ghost"
                onClick={ onRecovery }
              >
                { t('profile.passwordForgotten') }
              </Button>
            </div>
            <Button
              className="submit"
              error={ error }
              type="submit"
              disabled={ loading }
              onClick={ onSubmit }
              kind="danger"
            >
              { renderSubmitIcon() }
              { renderButtonMessage() }
            </Button>
          </>
        ) }
      </Form>
      { /* FIX: Use css to hide input so is being registered on first render */ }
      <div className="mfaSection" ref={ mfaSectionRef } style={ { display: sessionExpired ? 'none' : 'block' } }>
        <div className="mfaInput">
          <NWTextInput
            type="number"
            className="authCode"
            key="authCode"
            autoComplete="off"
            light
            required
            submitOnEnter={ onLoginVerify }
            labelText={ t('profile.authCode') }
            { ...sharedProps(fields.authCode) }
          />
          <span data-tooltip-id="verificationTooltip"><Info size={ 18 } /></span>
          <ReactTooltip
            className="verificationTooltip"
            id="verificationTooltip"
            variant="light"
            content={ (
              <span>{ t('profile.mfaTooltip') }</span>
            ) }
          />
        </div>
        <Button
          className="mfaSubmit"
          error={ error }
          type="submit"
          disabled={ loadingVerify }
          onClick={ onLoginVerify }
          kind="danger"
        >
          { loadingVerify && <Loading active small withOverlay={ false } /> }
          { t('common.validate') }
        </Button>
      </div>
    </StyledFormContainer>
  );
};

LoginForm.propTypes = {
  title: string,
  onChange: func.isRequired,
  onRemember: func,
  onSubmit: func.isRequired,
  loading: bool,
  successMessages: array,
  sessionExpired: bool,
  success: bool,
  recovered: bool,
  rememberMe: bool,
  recoveryEmail: string,
  message: string,
  error: string,
  mfaVerifyError: string,
  onRecovery: func,
  onCancelRecovery: func,
  onRecoverPasswordSubmit: func,
  isRecoveryMode: bool,
  username: string.isRequired,
  password: string.isRequired,
  isLoginPage: bool,
  onLoginVerify: func,
  validators: object,
  register: func,
  rhfErrors: object,
  loadingVerify: bool,
};

export default LoginForm;
